// src/app/quotes.jsx
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IoCalendar, IoAdd } from 'react-icons/io5';
import { MdAdd, MdRemove } from 'react-icons/md';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// Helper to fetch a token from localStorage
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

export default function Quotes() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Left panel: quotes list state
  const [quotes, setQuotes] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showLoadMore, setShowLoadMore] = useState(false);

  // Right panel: create quote form state
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [isNotesVisible, setIsNotesVisible] = useState(false);

  // Customer addition state
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    notes: '',
  });

  // Preview and permissions
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewQuoteData, setPreviewQuoteData] = useState({});
  const [businessInfo, setBusinessInfo] = useState({}); // Add business info state
  const [userPermissions, setUserPermissions] = useState(null);

  // Custom Modal state
  const [customModal, setCustomModal] = useState({
    open: false,
    title: '',
    message: '',
    buttons: []
  });

  // Email state for sending quotes
  const [sendingEmail, setSendingEmail] = useState(false);
  const [lastCreatedQuoteId, setLastCreatedQuoteId] = useState(null);

  const showCustomModal = (title, message, buttons) => {
    setCustomModal({ open: true, title, message, buttons });
  };

  const closeCustomModal = () => {
    setCustomModal({ open: false, title: '', message: '', buttons: [] });
  };

  // Permission check functions
  const hasQuotePermissions = () => {
    return userPermissions?.quote_perm !== 'none';
  };

  const hasQuoteWritePermissions = () => {
    return userPermissions?.quote_perm === 'write' || userPermissions?.quote_perm === undefined;
  };

  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          if (parsedPermissions?.quote_perm === 'none') {
            alert("You don't have permission to view quotes. Please contact your account administrator.");
            navigate('/');
            return;
          }
          
          // Only load data after permissions are confirmed
          await loadInitialData();
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Sequential data loading to prevent race conditions
  const loadInitialData = async () => {
    try {
      // Load customers first (needed for quote form)
      await fetchCustomers();
      
      // Then load quotes (small delay to prevent backend overwhelm)
      setTimeout(() => {
        setQuotes([]);
        setHasMore(true);
        setShowLoadMore(false);
        setPage(1);
        loadQuotes(1);
      }, 100);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  // Load customers for right panel form
  const fetchCustomers = async () => {
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_BASE_URL}/customers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const list = Array.isArray(data) ? data : data?.customers ?? [];
      setCustomers(list);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  // Load quotes with pagination
  const loadQuotes = async (pageNum = 1) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const token = getToken();
      const PAGE_SIZE = 25; // Match invoices.jsx pagination size
      const { data } = await axios.get(`${API_BASE_URL}/quotes?page=${pageNum}&page_size=${PAGE_SIZE}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const newQuotes = Array.isArray(data) ? data : data?.quotes ?? [];
      
      if (pageNum === 1) {
        setQuotes(newQuotes);
      } else {
        setQuotes(prevQuotes => [...prevQuotes, ...newQuotes]);
      }
      
      setPage(pageNum);
      
      // Check if there are more quotes to load - same logic as invoices.jsx
      const hasMoreQuotes = newQuotes.length >= PAGE_SIZE;
      setHasMore(hasMoreQuotes);
      setShowLoadMore(hasMoreQuotes && newQuotes.length > 0);
      
    } catch (error) {
      console.error('Error loading quotes:', error);
      setQuotes([]);
      setHasMore(false);
      setShowLoadMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle URL params for pre-filling form
  useEffect(() => {
    const pTitle = searchParams.get('title');
    const pDesc = searchParams.get('description');
    const pPrice = searchParams.get('price');
    const pCustomerName = searchParams.get('customerName');

    if (pTitle) setTitle(pTitle);
    if (pDesc) setDescription(pDesc);
    if (pPrice) setPrice(String(pPrice));
    if (pCustomerName) {
      setCustomerName(pCustomerName);
      setSearchQuery(pCustomerName);
      setSelectedCustomer(pCustomerName);
    }
  }, [searchParams]);

  const formatDate = (created) => {
    if (!created) return 'Invalid Date';
    const d = new Date(created);
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
    setCustomerName(q);
    if (!q.trim()) {
      setFilteredCustomers([]);
      return;
    }
    const t = q.toLowerCase();
    const matches = customers.filter((c) => (c?.name ?? '').toLowerCase().includes(t));
    setFilteredCustomers(matches);
  };

  const handleAddCustomer = async () => {
    try {
      const token = getToken();
      console.log('🚀 Creating customer with data:', newCustomer);
      
      const customerPayload = {
        ...newCustomer,
        notes: newCustomer.notes || '',
      };
      
      const { data } = await axios.post(`${API_BASE_URL}/customers/returnId`, customerPayload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      console.log('✅ Customer creation response:', data);

      // The API response might not have the name field, so use the original newCustomer.name
      const addedCustomer = {
        ...data,
        name: data.name || newCustomer.name, // Fallback to original name if API doesn't return it
        id: data.id || data._id || Date.now() // Handle different possible ID fields
      };

      // Use functional update to ensure we have the latest state
      setCustomers(prevCustomers => {
        const updatedCustomers = [...prevCustomers, addedCustomer];
        console.log('📝 Updated customers array:', updatedCustomers.map(c => c.name));
        return updatedCustomers;
      });

      // Set the customer as selected - use a timeout to ensure state is updated
      setTimeout(() => {
        setCustomerName(addedCustomer.name);
        setSearchQuery(addedCustomer.name);
        setSelectedCustomer(addedCustomer.name);
        setFilteredCustomers([]);
        setAddingCustomer(false);
        
        console.log('✅ Customer selected:', addedCustomer.name);
      }, 100);
      
      setNewCustomer({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: '',
        notes: '',
      });
      
      showCustomModal(
        'Customer Added Successfully! ✅',
        `${addedCustomer.name} has been added to your customer list and is now selected.`,
        [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
      );
    } catch (error) {
      console.error('Error adding customer:', error);
      console.error('Customer creation error response:', error.response?.data);
      
      let errorMessage = 'Failed to add customer. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid customer data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create customers.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Invalid customer data format. Please check all required fields are filled.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  // Helper function to format price input (only allow numbers and decimal point)
  const handlePriceChange = (text) => {
    // Remove any non-numeric characters except decimal point
    let cleaned = text.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      // Keep only first decimal point
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places if there's a decimal point
    if (parts.length === 2 && parts[1].length > 2) {
      cleaned = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    setPrice(cleaned);
  };

  // Email quote functionality (copied from quote-details.jsx)
  const emailQuotePDF = async (quoteId, customerData) => {
    if (!quoteId || sendingEmail) return;

    try {
      setSendingEmail(true);
      
      // Update the modal to show "Sending..." state
      showCustomModal(
        'Quote Created Successfully! 🎉',
        'Your quote has been created. What would you like to do next?',
        [
          {
            text: 'Sending…',
            style: 'primary',
            disabled: true,
            onPress: () => {} // Do nothing while sending
          },
          {
            text: '👁️ Preview Quote',
            style: 'secondary',
            onPress: () => {
              closeCustomModal();
              // Set preview data from saved quote data
              setPreviewQuoteData({
                customerName: customerData.name,
                title,
                description,
                price,
                notes,
              });
              setPreviewOpen(true);
            }
          },
          {
            text: '📋 View Quotes',
            style: 'default',
            onPress: closeCustomModal
          }
        ]
      );

      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/quotes/${quoteId}/email`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (response.data && response.data.success) {
        showCustomModal(
          'Email Sent Successfully! ✅',
          `Quote has been emailed as PDF to ${customerData.email}`,
          [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
        );
        
        // Update quotes list to reflect sent status
        setQuotes(prevQuotes => 
          prevQuotes.map(quote => 
            quote.id === quoteId 
              ? { ...quote, sentEmail: 'yes', dateSent: new Date().toISOString() }
              : quote
          )
        );
      } else {
        showCustomModal(
          'Email Status Unclear ⚠️',
          response.data?.message || 'Email may have been sent, but status is unclear.',
          [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
        );
      }
    } catch (emailError) {
      console.error('Error sending quote email:', emailError);
      
      let errorMessage = 'Failed to send email. Please try again.';
      
      if (emailError.response?.status === 400) {
        errorMessage = `Customer email issue. Please update customer information.`;
      } else if (emailError.response?.status === 404) {
        errorMessage = 'Quote not found.';
      } else if (emailError.response?.data?.detail) {
        errorMessage = emailError.response.data.detail;
      } else if (emailError.response?.data?.message) {
        errorMessage = emailError.response.data.message;
      } else if (emailError.message) {
        errorMessage = `Network error: ${emailError.message}`;
      }
      
      showCustomModal(
        'Email Failed ❌',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const saveQuote = async () => {
    const chosen = customers.find(
      (c) => (c?.name ?? '').trim().toLowerCase() === (selectedCustomer || searchQuery).trim().toLowerCase()
    );
    if (!chosen) {
      showCustomModal(
        'Error',
        'Please select a valid customer from the list or add a new one.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    // Ensure price defaults to "0.00" if blank or invalid
    let cleanedPrice = price && price.trim() !== '' ? price.trim() : '0.00';
    
    // Validate and format price
    if (cleanedPrice === '' || isNaN(parseFloat(cleanedPrice))) {
      cleanedPrice = '0.00';
    } else {
      // Ensure proper decimal formatting
      const numPrice = parseFloat(cleanedPrice);
      cleanedPrice = numPrice.toFixed(2);
    }

    const newQuote = {
      customer: chosen.name,
      customerId: chosen.id,
      title,
      description,
      price: cleanedPrice,
      notes,
    };

    const paramId = searchParams.get('id');
    if (paramId) newQuote.id = paramId;

    // Store quote data for preview modal before clearing form
    const quotePreviewData = {
      customerName: chosen.name,
      title,
      description,
      price: cleanedPrice,
      notes,
    };

    try {
      const token = getToken();
      const response = await axios.post(`${API_BASE_URL}/quotes`, newQuote, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      console.log('✅ Quote creation response:', response.data);

      const quoteId = response.data?.id;
      const businessData = response.data?.businessInfo; // Capture business info from backend

      // Store business info for preview
      if (businessData) {
        setBusinessInfo(businessData);
      }

      if (!quoteId) {
        console.error('❌ No quote ID returned from backend');
        showCustomModal(
          'Warning',
          'Quote was created but ID was not returned. Please try emailing from the quotes list.',
          [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
        );
        // Clear form and refresh list
        setTitle('');
        setDescription('');
        setPrice('');
        setNotes('');
        setSearchQuery('');
        setSelectedCustomer('');
        setCustomerName('');
        setFilteredCustomers([]);
        setIsNotesVisible(false);
        setAddingCustomer(false);
        
        setQuotes([]);
        setPage(1);
        setHasMore(true);
        await loadQuotes(1);
        return;
      }

      console.log('📧 Quote created successfully with ID:', quoteId);
      setLastCreatedQuoteId(quoteId);

      // Store preview data including business info
      setPreviewQuoteData({
        ...quotePreviewData,
        businessInfo: businessData
      });

      // Clear form
      setTitle('');
      setDescription('');
      setPrice('');
      setNotes('');
      setSearchQuery('');
      setSelectedCustomer('');
      setCustomerName('');
      setFilteredCustomers([]);
      setIsNotesVisible(false);
      setAddingCustomer(false);

      // Refresh quotes list
      setQuotes([]);
      setPage(1);
      setHasMore(true);
      await loadQuotes(1);

      // Show beautiful three-option modal matching mobile version
      showCustomModal(
        'Quote Created Successfully! 🎉',
        'Your quote has been created. What would you like to do next?',
        [
          {
            text: sendingEmail ? 'Sending…' : '📧 Email PDF to Customer',
            style: 'primary',
            disabled: sendingEmail,
            onPress: async () => {
              if (sendingEmail) return;
              
              if (!chosen?.email) {
                showCustomModal(
                  'Email Not Available ⚠️',
                  `Customer ${chosen?.name || quotePreviewData.customerName} does not have an email address on file. Please update customer information to enable emailing.`,
                  [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                );
                return;
              }

              // Call the actual email function with the quote ID
              await emailQuotePDF(quoteId, { name: chosen.name, email: chosen.email });
            }
          },
          {
            text: '👁️ Preview Quote',
            style: 'secondary',
            onPress: () => {
              closeCustomModal();
              // Set preview data from saved quote data
              setPreviewQuoteData(quotePreviewData);
              setPreviewOpen(true);
            }
          },
          {
            text: '📋 View Quotes',
            style: 'default',
            onPress: closeCustomModal
          }
        ]
      );
    } catch (error) {
      console.error('Error saving quote:', error);
      showCustomModal(
        'Error',
        'Failed to save quote. Please try again.',
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  const renderQuoteItem = ({ item }) => {
    const formattedDate = formatDate(item.created);

    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigate(`/quote-details?id=${item.id}`)}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.customer}>{item.customerName}</Text>
          <View style={styles.dateContainer}>
            <Text style={styles.dateText}>{`#${item.id}`}</Text>
            <IoCalendar size={16} color="green" style={{ marginHorizontal: 5 }} />
            <Text style={styles.dateText}>{formattedDate}</Text>
          </View>
        </View>
        <Text style={styles.title}>{item.title}</Text>
        {canSeePrices() && <Text style={styles.price}>${item.price}</Text>}
        <Text style={styles.quoteDetail}>
          Sent:{' '}
          {item.sentEmail === 'yes' && item.sentText === 'yes'
            ? 'Email, Text'
            : item.sentEmail === 'yes'
            ? 'Email'
            : item.sentText === 'yes'
            ? 'Text'
            : 'No'}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <KeyboardAvoidingView
      style={{ minHeight: '100vh', backgroundColor: '#fff' }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.page}>
        {/* Left Panel: Quotes List */}
        <View style={styles.leftCol}>
          <Text style={styles.header}>All Quotes</Text>

          {quotes.length === 0 && !isLoading ? (
            <Text style={styles.noQuotesText}>There are no quotes to display.</Text>
          ) : (
            <FlatList
              data={quotes}
              keyExtractor={(_, index) => index.toString()}
              renderItem={renderQuoteItem}
              contentContainerStyle={{ paddingBottom: 80 }}
              ListFooterComponent={
                showLoadMore && hasMore ? (
                  <TouchableOpacity
                    style={styles.loadMoreButton}
                    onPress={() => loadQuotes(page + 1)}
                    disabled={isLoading}
                  >
                    <Text style={styles.loadMoreText}>{isLoading ? 'Loading...' : 'Load More'}</Text>
                  </TouchableOpacity>
                ) : null
              }
            />
          )}

          {!hasQuoteWritePermissions() && (
            <Text style={styles.readOnlyText}>You have read-only access to quotes.</Text>
          )}
        </View>

        {/* Right Panel: Create Quote Form */}
        {hasQuoteWritePermissions() && (
          <View style={styles.rightCol}>
            <Text style={styles.formHeader}>Create New Quote</Text>

            {/* Customer selection */}
            <Text style={styles.label}>Customer Name</Text>
            <TextInput
              style={styles.input}
              value={searchQuery}
              onChangeText={handleSearch}
              onBlur={() => {
                if (customers.some((c) => c.name === searchQuery)) setFilteredCustomers([]);
              }}
              placeholder="Search or add customer"
              autoComplete="off"
              autoCorrect={false}
            />

            {searchQuery ? (
              <View style={styles.searchResults}>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer, index) => (
                    <TouchableOpacity
                      key={customer.id || `c-${index}`}
                      onPress={() => {
                        setCustomerName(customer.name);
                        setSearchQuery(customer.name);
                        setSelectedCustomer(customer.name);
                        setFilteredCustomers([]);
                      }}
                    >
                      <Text style={styles.searchResult}>{customer.name}</Text>
                    </TouchableOpacity>
                  ))
                ) : !customers.some((c) => c.name === searchQuery) ? (
                  <View style={styles.addCustomerRow}>
                    <Text style={styles.searchResult}>{searchQuery}</Text>
                    <TouchableOpacity
                      style={styles.addCustomerButton}
                      onPress={() => {
                        setNewCustomer((nc) => ({ ...nc, name: searchQuery }));
                        setAddingCustomer(true);
                      }}
                    >
                      <MdAdd size={20} color="green" />
                      <Text style={styles.addCustomerText}>Add Customer</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
            ) : null}

            {/* Add Customer Form */}
            {addingCustomer && (
              <View style={styles.inlineModal}>
                <Text style={styles.modalHeader}>Add Customer</Text>
                {[
                  ['Customer Name', 'name'],
                  ['Address', 'address'],
                  ['City', 'city'],
                  ['State', 'state'],
                  ['Zip', 'zip'],
                  ['Phone', 'phone'],
                  ['Email', 'email'],
                  ['Notes', 'notes'],
                ].map(([label, key]) => (
                  <View key={key} style={{ marginBottom: 10 }}>
                    <Text style={styles.modalLabel}>{label}</Text>
                    <TextInput
                      style={styles.input}
                      value={newCustomer[key]}
                      onChangeText={(t) => setNewCustomer((nc) => ({ ...nc, [key]: t }))}
                      placeholder={label}
                      multiline={key === 'notes'}
                    />
                  </View>
                ))}
                <TouchableOpacity style={styles.saveButton} onPress={handleAddCustomer}>
                  <Text style={styles.saveButtonText}>Save Customer</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.returnButton} onPress={() => setAddingCustomer(false)}>
                  <Text style={styles.returnButtonText}>Return To Quote</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Quote Fields */}
            <Text style={styles.label}>Title</Text>
            <TextInput 
              style={styles.input} 
              value={title} 
              onChangeText={setTitle}
            />

            <Text style={styles.label}>Description</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={description}
              onChangeText={setDescription}
              multiline
            />

            <Text style={styles.label}>Price</Text>
            <TextInput
              style={styles.input}
              value={price}
              onChangeText={handlePriceChange}
              inputMode="decimal"
              keyboardType="numeric"
              placeholder="0.00"
            />

            {/* Notes Section */}
            {!isNotesVisible ? (
              <TouchableOpacity style={styles.addNotesButton} onPress={() => setIsNotesVisible(true)}>
                <MdAdd size={20} color="green" />
                <Text style={styles.addNotesText}>Add Notes</Text>
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity style={styles.hideNotesButton} onPress={() => setIsNotesVisible(false)}>
                  <MdRemove size={20} color="red" />
                  <Text style={styles.hideNotesText}>Hide Notes</Text>
                </TouchableOpacity>
                <View style={styles.fieldContainer}>
                  <Text style={styles.label}>Notes (for internal use only):</Text>
                  <TextInput
                    style={[styles.input, styles.textArea]}
                    placeholder="Enter notes here..."
                    multiline
                    numberOfLines={4}
                    onChangeText={setNotes}
                    value={notes}
                  />
                </View>
              </>
            )}

            <TouchableOpacity style={styles.saveButton} onPress={saveQuote}>
              <Text style={styles.saveButtonText}>Save Quote</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.clearButton} onPress={() => {
              setTitle('');
              setDescription('');
              setPrice('');
              setNotes('');
              setSearchQuery('');
              setSelectedCustomer('');
              setCustomerName('');
              setFilteredCustomers([]);
              setIsNotesVisible(false);
              setAddingCustomer(false);
              setNewCustomer({
                name: '',
                address: '',
                city: '',
                state: '',
                zip: '',
                phone: '',
                email: '',
                notes: '',
              });
            }}>
              <Text style={styles.clearButtonText}>Clear Form</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Professional Quote Preview overlay */}
      {previewOpen && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <iframe
              srcDoc={`
                <!DOCTYPE html>
                <html>
                <head>
                  <meta charset="UTF-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Quote Q-${lastCreatedQuoteId || 'XXXX'} - ${previewQuoteData.customerName || 'Customer'}</title>
                  <style>
                    @media print {
                      body { margin: 0; }
                      .action-buttons { display: none; }
                    }
                    
                    body {
                      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                      line-height: 1.6;
                      color: #333;
                      max-width: 800px;
                      margin: 0 auto;
                      padding: 60px 30px 100px 30px;
                      background: #fff;
                    }
                    
                    .header {
                      display: flex;
                      justify-content: space-between;
                      align-items: flex-start;
                      margin-bottom: 40px;
                      padding-bottom: 20px;
                      border-bottom: 3px solid #2563eb;
                      margin-top: 80px;
                    }
                    
                    .company-info {
                      flex: 1;
                    }
                    
                    .company-name {
                      font-size: 28px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-bottom: 10px;
                    }
                    
                    .company-details {
                      color: #666;
                      font-size: 14px;
                    }
                    
                    .quote-info {
                      text-align: right;
                      flex: 1;
                    }
                    
                    .quote-title {
                      font-size: 32px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-bottom: 10px;
                    }
                    
                    .quote-details {
                      font-size: 14px;
                      color: #666;
                    }
                    
                    .customer-section {
                      margin-bottom: 30px;
                    }
                    
                    .section-title {
                      font-size: 18px;
                      font-weight: bold;
                      color: #374151;
                      margin-bottom: 15px;
                      padding-bottom: 5px;
                      border-bottom: 2px solid #e5e7eb;
                    }
                    
                    .customer-info {
                      background: #f8fafc;
                      padding: 20px;
                      border-radius: 8px;
                      border-left: 4px solid #2563eb;
                    }
                    
                    .customer-name {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 10px;
                    }
                    
                    .services-section {
                      margin-bottom: 30px;
                    }
                    
                    .service-item {
                      background: #fff;
                      border: 1px solid #e5e7eb;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 20px;
                      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    }
                    
                    .service-title {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 15px;
                    }
                    
                    .service-description {
                      color: #4b5563;
                      margin-bottom: 20px;
                      line-height: 1.7;
                      white-space: pre-wrap;
                    }
                    
                    .pricing-section {
                      background: #f8fafc;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 30px;
                      border: 2px solid #2563eb;
                    }
                    
                    .price-row {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      padding: 15px 0;
                      border-bottom: 1px solid #e5e7eb;
                    }
                    
                    .price-row:last-child {
                      border-bottom: none;
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                      margin-top: 10px;
                      padding-top: 20px;
                      border-top: 2px solid #2563eb;
                    }
                    
                    .price-label {
                      font-size: 18px;
                      color: #374151;
                    }
                    
                    .price-amount {
                      font-size: 18px;
                      font-weight: bold;
                      color: #1f2937;
                    }
                    
                    .total-label {
                      font-size: 20px;
                      font-weight: bold;
                    }
                    
                    .total-amount {
                      font-size: 24px;
                      font-weight: bold;
                      color: #2563eb;
                    }
                    
                    .terms-section {
                      margin-top: 40px;
                      padding: 20px;
                      background: #fefce8;
                      border-radius: 8px;
                      border-left: 4px solid #eab308;
                    }
                    
                    .terms-title {
                      font-size: 16px;
                      font-weight: bold;
                      color: #92400e;
                      margin-bottom: 10px;
                    }
                    
                    .terms-text {
                      font-size: 14px;
                      color: #a16207;
                      line-height: 1.6;
                    }
                    
                    .footer {
                      margin-top: 50px;
                      padding-top: 20px;
                      border-top: 2px solid #e5e7eb;
                      text-align: center;
                      color: #6b7280;
                      font-size: 14px;
                    }
                    
                    .action-buttons {
                      position: fixed;
                      top: 20px;
                      right: 20px;
                      display: flex;
                      flex-direction: row;
                      gap: 10px;
                      z-index: 1000;
                      background: rgba(255,255,255,0.95);
                      padding: 10px;
                      border-radius: 10px;
                      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    }
                    
                    .action-button {
                      background: #2563eb;
                      color: white;
                      border: none;
                      padding: 12px 20px;
                      border-radius: 8px;
                      font-size: 14px;
                      font-weight: bold;
                      cursor: pointer;
                      box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3);
                      transition: all 0.2s;
                    }
                    
                    .action-button:hover {
                      background: #1d4ed8;
                      transform: translateY(-2px);
                      box-shadow: 0 4px 8px rgba(37, 99, 235, 0.4);
                    }
                    
                    .save-pdf-button {
                      background: #059669;
                    }
                    
                    .save-pdf-button:hover {
                      background: #047857;
                    }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <div class="company-info">
                      <div class="company-name">${(previewQuoteData.businessInfo?.businessName || businessInfo?.businessName || 'Your Business Name')}</div>
                      <div class="company-details">
                        ${(() => {
                          const bInfo = previewQuoteData.businessInfo || businessInfo || {};
                          const addressParts = [];
                          
                          if (bInfo.address) addressParts.push(bInfo.address);
                          if (bInfo.city && bInfo.state) {
                            const cityState = bInfo.zip ? `${bInfo.city}, ${bInfo.state} ${bInfo.zip}` : `${bInfo.city}, ${bInfo.state}`;
                            addressParts.push(cityState);
                          } else if (bInfo.city) {
                            addressParts.push(bInfo.city);
                          } else if (bInfo.state) {
                            addressParts.push(bInfo.state);
                          }
                          
                          if (bInfo.phone) {
                            addressParts.push(`Phone: ${bInfo.phone}`);
                          }
                          if (bInfo.email) {
                            addressParts.push(`Email: ${bInfo.email}`);
                          }
                          
                          // Add default contact info only if no business info available at all
                          if (addressParts.length === 0 && !bInfo.businessName) {
                            return `123 Business Street<br>City, State 12345<br>Phone: (555) 123-4567<br>Email: info@yourbusiness.com`;
                          }
                          
                          return addressParts.join('<br>') || 'Contact information not available';
                        })()}
                      </div>
                    </div>
                    <div class="quote-info">
                      <div class="quote-title">QUOTE</div>
                      <div class="quote-details">
                        <strong>Quote #:</strong> Q-${lastCreatedQuoteId || 'XXXX'}<br>
                        <strong>Date:</strong> ${new Date().toLocaleDateString()}<br>
                        <strong>Valid Until:</strong> ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div class="customer-section">
                    <div class="section-title">Quote For:</div>
                    <div class="customer-info">
                      <div class="customer-name">${previewQuoteData.customerName || 'Customer Name'}</div>
                      ${(() => {
                        const customer = customers.find(c => c.name === previewQuoteData.customerName);
                        if (customer) {
                          return `
                            ${customer.address ? `<div>${customer.address}</div>` : ''}
                            ${customer.city && customer.state ? `<div>${customer.city}, ${customer.state} ${customer.zip || ''}</div>` : ''}
                            ${customer.phone ? `<div>Phone: ${customer.phone}</div>` : ''}
                            ${customer.email ? `<div>Email: ${customer.email}</div>` : ''}
                          `;
                        }
                        return '';
                      })()}
                    </div>
                  </div>

                  <div class="services-section">
                    <div class="section-title">Services</div>
                    <div class="service-item">
                      <div class="service-title">${previewQuoteData.title || 'Service Title'}</div>
                      <div class="service-description">${previewQuoteData.description || 'Service description'}</div>
                    </div>
                  </div>

                  <div class="pricing-section">
                    <div class="price-row">
                      <span class="price-label">Service Total:</span>
                      <span class="price-amount">$${parseFloat(previewQuoteData.price || 0).toFixed(2)}</span>
                    </div>
                    <div class="price-row">
                      <span class="total-label">Total Amount:</span>
                      <span class="total-amount">$${parseFloat(previewQuoteData.price || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <div class="terms-section">
                    <div class="terms-title">Quote Terms & Conditions</div>
                    <div class="terms-text">
                      • This quote is valid for 30 days from the date above<br>
                      • Prices are subject to change without notice<br>
                      • A 50% deposit may be required to begin work<br>
                      • Final pricing may vary based on actual work performed<br>
                      • Additional charges may apply for work outside the scope described above
                    </div>
                  </div>

                  <div class="footer">
                    <p>Thank you for considering our services. We look forward to working with you!</p>
                    <p><strong>Questions?</strong> Contact us${(() => {
                      const bInfo = previewQuoteData.businessInfo || businessInfo || {};
                      const contactParts = [];
                      
                      // Only add phone if it exists in business info
                      if (bInfo.phone) {
                        contactParts.push(`at ${bInfo.phone}`);
                      }
                      
                      // Only add email if it exists in business info  
                      if (bInfo.email) {
                        contactParts.push(`${contactParts.length > 0 ? 'or ' : 'at '}${bInfo.email}`);
                      }
                      
                      // Fallback to defaults if no contact info available
                      if (contactParts.length === 0) {
                        contactParts.push('at (555) 123-4567 or info@yourbusiness.com');
                      }
                      
                      return contactParts.length > 0 ? ' ' + contactParts.join(' ') : '';
                    })()}</p>
                  </div>

                  <div class="action-buttons">
                    <button class="action-button save-pdf-button" onclick="window.print()">Save as PDF</button>
                    <button class="action-button" onclick="window.close()">Close Preview</button>
                  </div>
                </body>
                </html>
              `}
              style={{
                width: '100%',
                height: '80vh',
                border: 'none',
                borderRadius: '8px'
              }}
              title="Quote Preview"
            />
            
            <View style={styles.buttonGroup}>
              <TouchableOpacity 
                style={[styles.actionButton, styles.emailButton, sendingEmail && { opacity: 0.7 }]}
                onPress={async () => {
                  const chosen = customers.find(
                    (c) => (c?.name ?? '').trim().toLowerCase() === (previewQuoteData.customerName || '').trim().toLowerCase()
                  );
                  if (!chosen?.email) {
                    showCustomModal(
                      'Email Not Available ⚠️',
                      'Customer email not found. Please update customer information.',
                      [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                    );
                    return;
                  }
                  
                  setPreviewOpen(false);
                  if (lastCreatedQuoteId) {
                    await emailQuotePDF(lastCreatedQuoteId, { name: chosen.name, email: chosen.email });
                  } else {
                    showCustomModal(
                      'Error',
                      'Quote ID not available. Please try emailing from the quotes list.',
                      [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                    );
                  }
                }}
                disabled={sendingEmail}
              >
                <Text style={styles.actionButtonText}>
                  {sendingEmail ? 'Sending…' : '📧 Send to Customer'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity style={[styles.saveButton, { marginTop: 16 }]} onPress={() => {
                setPreviewOpen(false);
                // Show the three-option modal again when closing preview
                showCustomModal(
                  'Quote Created Successfully! 🎉',
                  'Your quote has been created. What would you like to do next?',
                  [
                    {
                      text: sendingEmail ? 'Sending…' : '📧 Email PDF to Customer',
                      style: 'primary',
                      disabled: sendingEmail,
                      onPress: async () => {
                        if (sendingEmail) return;
                        
                        const chosen = customers.find(
                          (c) => (c?.name ?? '').trim().toLowerCase() === (previewQuoteData.customerName || '').trim().toLowerCase()
                        );
                        
                        if (!chosen?.email) {
                          showCustomModal(
                            'Email Not Available ⚠️',
                            `Customer ${chosen?.name || previewQuoteData.customerName} does not have an email address on file. Please update customer information to enable emailing.`,
                            [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                          );
                          return;
                        }

                        // Call the actual email function with the quote ID
                        if (lastCreatedQuoteId) {
                          await emailQuotePDF(lastCreatedQuoteId, { name: chosen.name, email: chosen.email });
                        } else {
                          showCustomModal(
                            'Error',
                            'Quote ID not available. Please try emailing from the quotes list.',
                            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                          );
                        }
                      }
                    },
                    {
                      text: '👁️ Preview Quote',
                      style: 'secondary',
                      onPress: () => {
                        closeCustomModal();
                        setPreviewOpen(true);
                      }
                    },
                    {
                      text: '📋 View Quotes',
                      style: 'default',
                      onPress: closeCustomModal
                    }
                  ]
                );
              }}>
                <Text style={styles.saveButtonText}>Close Preview</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Custom Modal */}
      {customModal.open && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <Text style={styles.previewTitle}>{customModal.title}</Text>
            <Text>{customModal.message}</Text>
            <View style={styles.buttonGroup}>
              {customModal.buttons.map((button, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.actionButton,
                    button.style === 'primary' && { backgroundColor: 'blue' },
                    button.style === 'secondary' && { backgroundColor: 'gray' },
                    button.style === 'danger' && { backgroundColor: 'red' },
                    button.style === 'default' && { backgroundColor: '#f0f0f0' },
                  ]}
                  onPress={button.onPress}
                >
                  <Text style={[
                    styles.actionButtonText,
                    button.style === 'default' && { color: 'black' },
                    button.style !== 'default' && { color: 'white' }
                  ]}>
                    {button.text}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    backgroundColor: '#fff',
    flexDirection: 'row',
    gap: 16,
    padding: 16,
  },

  // Left panel: quotes list
  leftCol: {
    flex: 1,
    minWidth: 0,
  },

  // Right panel: create form
  rightCol: {
    width: 620,
    maxWidth: '40vw',
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 10,
    padding: 16,
    position: 'sticky',
    top: 16,
    alignSelf: 'flex-start',
  },

  // Headers
  header: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
  formHeader: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },

  // Quote cards
  card: {
    backgroundColor: '#f0f0f0',
    padding: 15,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 8,
    marginBottom: 15,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  customer: { fontSize: 16, fontWeight: 'bold', marginBottom: 5 },
  title: { fontSize: 14, marginBottom: 5 },
  price: { fontSize: 14, color: '#007BFF' },
  quoteDetail: { fontSize: 12, color: '#555', marginTop: 5 },
  dateContainer: { flexDirection: 'row', alignItems: 'center', gap: 0 },
  dateText: { fontSize: 14, color: 'green', fontWeight: 'bold', marginHorizontal: 6 },

  // Form elements
  label: { marginTop: 15, fontWeight: 'bold' },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 8,
    borderRadius: 4,
    marginTop: 6,
    backgroundColor: '#fff',
  },
  textArea: { height: 100, textAlignVertical: 'top' },

  // Customer search
  searchResults: { marginTop: 5, backgroundColor: '#f9f9f9', borderRadius: 4, padding: 10 },
  searchResult: { paddingVertical: 5, fontSize: 14 },
  addCustomerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  addCustomerButton: { flexDirection: 'row', alignItems: 'center', marginLeft: 10 },
  addCustomerText: { color: 'green', marginLeft: 5 },

  // Inline customer modal
  inlineModal: {
    marginTop: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  modalHeader: { fontSize: 18, fontWeight: 'bold', marginBottom: 20 },
  modalLabel: { marginTop: 15, fontWeight: 'bold', fontSize: 14 },

  // Notes
  addNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 15 },
  addNotesText: { color: 'green', marginLeft: 5 },
  hideNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 15 },
  hideNotesText: { color: 'red', marginLeft: 5 },
  fieldContainer: { marginTop: 0 },

  // Buttons
  saveButton: { marginTop: 20, backgroundColor: 'blue', padding: 12, borderRadius: 6, alignItems: 'center' },
  saveButtonText: { color: 'white', fontWeight: 'bold' },
  clearButton: { marginTop: 10, backgroundColor: 'red', padding: 12, borderRadius: 6, alignItems: 'center' },
  clearButtonText: { color: 'white', fontWeight: 'bold' },
  returnButton: { backgroundColor: '#ccc', padding: 10, borderRadius: 5, alignItems: 'center', marginTop: 10 },
  returnButtonText: { color: '#000', fontSize: 16, fontWeight: 'bold' },
  loadMoreButton: { padding: 10, backgroundColor: '#007BFF', borderRadius: 5, alignItems: 'center', marginVertical: 10 },
  loadMoreText: { color: '#fff', fontSize: 16 },

  // Status messages
  noQuotesText: { textAlign: 'center', fontSize: 16, color: '#777', marginTop: 50 },
  readOnlyText: { textAlign: 'center', fontSize: 16, color: '#777', marginTop: 20 },

  // Preview overlay
  previewOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
  },
  previewCard: {
    width: 'min(640px, 100%)',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  previewTitle: { fontSize: 20, fontWeight: '700', marginBottom: 12 },
  bold: { fontWeight: '700' },
  buttonGroup: { marginTop: 16 },
  actionButton: {
    backgroundColor: '#f0f0f0',
    padding: 12,
    borderRadius: 6,
    alignItems: 'center',
    marginBottom: 10,
  },
  actionButtonText: { color: 'black', fontWeight: 'bold' },
  pdfButton: { backgroundColor: '#d3d3d3' },
  emailButton: { backgroundColor: '#d3d3d3' },
});
